import React from "react";
import TopRightSemiCircular from "../assets/img/common/semi-circular-top-right.png";
import LeftSemiCircular from "../assets/img/common/semi-cicular-left.png";
import { ReactComponent as SearchIcon } from "../assets/img/common/search-icon.svg";
import { useState } from "react";
import ProjectCard from "../components/common/ProjectCard";
import Container from "../components/common/Container";
import { categories, projectStates } from "../data/dataCheckboxes";
import ToggleTag from "../components/common/ToggleTag";
import { useGetProjectsQuery } from "../redux/slices/api/projectsApiSlice";
import { Bars } from "react-loader-spinner";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import useFilterData from "../hooks/useFilterData";
import { FiFilter, FiPlus, FiArrowRight, FiInfo } from "react-icons/fi";
import { HiSwitchHorizontal, HiOutlineBeaker } from "react-icons/hi";
import ProjectFilters from "../components/projects/ProjectFilters";
import MetaTags from "../components/common/MetaTags";
import { useSelector, useDispatch } from "react-redux";
import { selectGeneral } from "../redux/slices/generalSlice";
import { useLazyGetUserQuery } from "../redux/slices/api/userApi";
import useHandleCreateProject from "../hooks/useHandleCreateProject";
const Projects = () => {
  // State to manage the selected currency token
  const [currencyToken, setCurrencyToken] = useState("");

  // State to control the visibility of the filters popup
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);

  // Query to fetch projects data
  const { data: projectsData, isLoading } = useGetProjectsQuery();

  // Access the current URL location and extract query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const keywords = queryParams.get("keywords");

  // Redux state
  const { isLoggedIn } = useSelector(selectGeneral);
  const dispatch = useDispatch();

  // Get user data to check role
  const [triggerGetUser, { data: userData }] = useLazyGetUserQuery();

  useEffect(() => {
    if (isLoggedIn) {
      triggerGetUser();
    }
  }, [isLoggedIn, triggerGetUser]);

  // Check user role for creating projects
  const userRole = userData?.user?.role;
  const canCreateProject = isLoggedIn && userRole && ['applicant', 'organization'].includes(userRole);
  const hasCompletedProfile = isLoggedIn && userRole && userRole !== 'un_registered';

  // Hook for handling project creation
  const { handleCreateProject } = useHandleCreateProject();

  // Initial filter object for projects
  const initialFilter = {
    search: "",
    keywords: keywords?.split(",") ?? [],
    category: [],
    projectState: [],
    rangePrice: {},
    token: "",
  };

  // Functions to check various conditions for filtering projects
  const checkSearch = (search, item) =>
    item?.title?.toLowerCase()?.includes(search.toLowerCase()) ||
    item?.summary?.toLowerCase()?.includes(search?.toLowerCase());
  const checkCategory = (category, item) => category.includes(item.category);
  const checkKeywords = (keywords, item) =>
    item.keywords.some((key) => keywords.includes(key));
  const checkProjectState = (state, item) => state.includes(item.state);
  const checkCurrencyToken = (token, item) => {
    if (token === "Cross-chain") return !item?.token;
    else if (token === "" || token === "all") return true;
    return item?.token === token;
  };

  const checkRangePrice = (rangePrice, item) =>
    (currencyToken === "" || item?.token === currencyToken) &&
    item?.total_budget >= rangePrice.minPrice &&
    item?.total_budget <= rangePrice.maxPrice;

  // Custom hook to handle filtering of projects data
  const {
    filteredData: filteredProjects,
    filter,
    setFilter,
    handleChangeFilter,
    handleRemoveFilter,
  } = useFilterData({
    initialData: projectsData,
    initialFilter,
    initialDataLoading: isLoading,
    checkers: [
      {
        key: "search",
        checker: checkSearch,
      },
      {
        key: "category",
        checker: checkCategory,
      },
      {
        key: "keywords",
        checker: checkKeywords,
      },
      {
        key: "projectState",
        checker: checkProjectState,
      },
      {
        key: "rangePrice",
        checker: checkRangePrice,
      },
      {
        key: "token",
        checker: checkCurrencyToken,
      },

    ],
  });

  // Effect to update filter keywords when the 'keywords' query parameter changes
  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      keywords: keywords && keywords !== null ? keywords?.split(",") : [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywords]);

  // Function to handle checkbox changes for filtering
  const handleChangeCheckBox = (e, property, value) => {
    e.target.checked
      ? handleChangeFilter({ [property]: value }, true)
      : handleRemoveFilter({ [property]: value });
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen flex justify-center items-center">
        <Bars
          height="50"
          width="50"
          color="#8538ff"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
      </div>
    );
  } else
    return (
      <div
        className="w-full min-h-screen bg-no-repeat roboto-font"
        style={{
          backgroundImage: `url(${TopRightSemiCircular}), url(${LeftSemiCircular})`,
          backgroundPosition: "top right, left top",
        }}
      >
        <MetaTags
          title="Projects"
          description={`NFT- Fundraising By FunDeSci An innovative approach for decentralized crowdfunding of scientific projects`}
          link="/projects"
        />

        <Container className="flex flex-col items-center pt-28 pb-10">
          {/* Hero Section */}
          <div className="w-full mb-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-3 abel-font bg-gradient-to-r from-[#8538ff] to-[#7e7cff] bg-clip-text text-transparent">
              Explore Research Projects
            </h1>
            <p className="text-base text-gray-600 max-w-xl mx-auto">
              Discover and fund groundbreaking scientific research through decentralized crowdfunding
            </p>
          </div>

          {/* Create Project Banner - Contextual based on user state */}
          <div className="w-full mb-8">
            {isLoggedIn && canCreateProject ? (
              // Logged in with completed profile - Show create button
              <div className="w-full bg-gradient-to-r from-[#8538ff] via-[#9c5cff] to-[#7e7cff] rounded-2xl p-6 shadow-lg">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4 text-white">
                    <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                      <HiOutlineBeaker className="text-2xl" />
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-xl font-bold">Fund Your Research</h3>
                      <p className="text-white/80 text-sm">Create a fundraising project and get support from the community</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCreateProject}
                    className="flex items-center gap-2 px-6 py-3 bg-white text-[#8538ff] font-semibold rounded-xl hover:bg-white/90 transition-all shadow-md hover:shadow-lg"
                  >
                    <FiPlus className="text-lg" />
                    Create Project
                    <FiArrowRight className="text-lg" />
                  </button>
                </div>
              </div>
            ) : isLoggedIn && !hasCompletedProfile ? (
              // Logged in but incomplete profile - Guide to complete profile
              <div className="w-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                      <FiInfo className="text-2xl text-amber-600" />
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-lg font-bold text-gray-800">Complete Your Profile</h3>
                      <p className="text-gray-600 text-sm">Finish setting up your profile to create and manage research projects</p>
                    </div>
                  </div>
                  <Link
                    to="/create-profile"
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:opacity-90 transition-all shadow-md"
                  >
                    <HiSwitchHorizontal className="text-lg" />
                    Complete Profile
                  </Link>
                </div>
              </div>
            ) : (
              // Not logged in - Guide to sign up/login
              <div className="w-full bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                      <HiOutlineBeaker className="text-2xl text-gray-500" />
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-lg font-bold text-gray-800">Have a Research Project?</h3>
                      <p className="text-gray-600 text-sm">Sign up to start fundraising for your scientific research</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCreateProject}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#8538ff] to-[#7e7cff] text-white font-medium rounded-xl hover:opacity-90 transition-all shadow-md"
                  >
                    Get Started
                    <FiArrowRight className="text-lg" />
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Main Content */}
        <Container className="py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Desktop */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Search */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center shadow-md">
                      <SearchIcon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-secondary">Search</h3>
                  </div>
                  <div className="relative">
                    <input
                      type="search"
                      className="w-full px-4 py-3 pl-11 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-sm"
                      placeholder="Search projects..."
                      onChange={(e) => handleChangeFilter({ search: e.target.value })}
                      value={filter.search}
                    />
                    <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Categories */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center shadow-md">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-secondary">Categories</h3>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto custom-primary-tiny-scroll pr-2">
                    {categories.map((item, index) => (
                      <ToggleTag
                        key={item?.id + index}
                        Icon={item.icon}
                        id={item?.id}
                        title={item?.title}
                        onChange={(e) => handleChangeCheckBox(e, "category", item?.title)}
                        checked={filter.category.includes(item?.title)}
                        compact={true}
                      />
                    ))}
                  </div>
                </div>

                {/* Project Status */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-[#7e7cff] flex items-center justify-center shadow-md">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-secondary">Status</h3>
                  </div>
                  <div className="space-y-2">
                    {projectStates?.map((state, index) => (
                      <label 
                        key={state + index} 
                        className="flex items-center gap-3 p-3 rounded-xl bg-white/40 border border-white/30 hover:border-primary/50 hover:bg-primary/10 transition-all cursor-pointer group backdrop-blur-sm"
                      >
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-primary cursor-pointer"
                          checked={filter?.projectState?.includes(state)}
                          onChange={(e) => handleChangeCheckBox(e, "projectState", state)}
                        />
                        <span className="text-sm font-medium text-gray-700 group-hover:text-primary transition-colors flex-1">
                          {state}
                        </span>
                        {filter?.projectState?.includes(state) && (
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Mobile Filter Button */}
            <div className="lg:hidden fixed bottom-6 right-6 z-40">
              <button
                onClick={() => setShowFiltersPopup(true)}
                className="flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-primary to-[#7e7cff] text-white rounded-full shadow-2xl hover:shadow-primary/50 transition-all duration-300 font-semibold"
              >
                <FiFilter size={20} />
                Filters
                {(filter.category.length > 0 || filter.projectState.length > 0) && (
                  <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {filter.category.length + filter.projectState.length}
                  </span>
                )}
              </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              {/* Search Bar - Mobile */}
              <div className="lg:hidden mb-6 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-4">
                <div className="relative">
                  <input
                    type="search"
                    className="w-full px-4 py-3 pl-11 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all text-sm"
                    placeholder="Search projects..."
                    onChange={(e) => handleChangeFilter({ search: e.target.value })}
                    value={filter.search}
                  />
                  <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {/* Results Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-secondary">
                    {filteredProjects?.length || 0} {filteredProjects?.length === 1 ? 'Project' : 'Projects'}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    {filter.category.length > 0 || filter.projectState.length > 0 
                      ? 'Filtered results' 
                      : 'All available projects'}
                  </p>
                </div>
              </div>

              {/* Project Cards Grid */}
              <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-20">
                {filteredProjects?.length ? (
                  filteredProjects?.map((project, index) => (
                    <ProjectCard key={index} data={project} />
                  ))
                ) : (
                  <div className="col-span-full flex flex-col items-center justify-center py-20 px-4">
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No Projects Found</h3>
                    <p className="text-gray-500 text-center max-w-md">
                      Try adjusting your filters or search terms to find what you're looking for.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
        </Container>

        {/* Mobile Filter Modal */}
        {showFiltersPopup && (
          <ProjectFilters
            show={showFiltersPopup}
            setShow={setShowFiltersPopup}
            projectsData={projectsData}
            isLoading={isLoading}
            handleChangeCheckBox={handleChangeCheckBox}
            handleChangeFilter={handleChangeFilter}
            handleRemoveFilter={handleRemoveFilter}
            setShowFiltersPopup={setShowFiltersPopup}
            filter={filter}
            setFilter={setFilter}
            initialFilter={initialFilter}
            setCurrencyToken={setCurrencyToken}
          />
        )}
      </div>
    );
};

export default Projects;
